import { Injectable, signal } from '@angular/core';

export type NotificationType = 'success' | 'error' | 'warning' | 'info';

export interface Notification {
	id: number;
	type: NotificationType;
	title?: string;
	message: string;
	duration: number;
}

interface NotificationTimer {
	endAt: number;
	remaining: number;
	timeoutId: ReturnType<typeof setTimeout>;
}

@Injectable({ providedIn: 'root' })
export class NotificationService {
	private nextId = 1;
	private readonly timers = new Map<number, NotificationTimer>();
	private readonly notificationsSignal = signal<Notification[]>([]);
	readonly notifications = this.notificationsSignal.asReadonly();

	success(message: string, title?: string, duration = 5000): void {
		this.show('success', message, title, duration);
	}

	error(message: string, title?: string, duration = 5000): void {
		this.show('error', message, title, duration);
	}

	warning(message: string, title?: string, duration = 5000): void {
		this.show('warning', message, title, duration);
	}

	info(message: string, title?: string, duration = 5000): void {
		this.show('info', message, title, duration);
	}

	remove(id: number): void {
		this.clearTimer(id);
		this.notificationsSignal.update((notifications) => notifications.filter((notification) => notification.id !== id));
	}

	clear(): void {
		for (const id of this.timers.keys()) {
			this.clearTimer(id);
		}

		this.notificationsSignal.set([]);
	}

	pause(id: number): void {
		const timer = this.timers.get(id);
		if (!timer) {
			return;
		}

		globalThis.clearTimeout(timer.timeoutId);
		timer.remaining = Math.max(0, timer.endAt - Date.now());
	}

	resume(id: number): void {
		const timer = this.timers.get(id);
		if (!timer) {
			return;
		}

		globalThis.clearTimeout(timer.timeoutId);
		timer.endAt = Date.now() + timer.remaining;
		timer.timeoutId = globalThis.setTimeout(() => this.remove(id), timer.remaining);
	}

	private show(type: NotificationType, message: string, title?: string, duration = 5000): void {
		const duplicate = this.notificationsSignal().some(
			(notification) => notification.type === type && notification.message === message && notification.title === title,
		);

		if (duplicate) {
			return;
		}

		const notification: Notification = {
			id: this.nextId++,
			type,
			title,
			message,
			duration,
		};

		this.notificationsSignal.update((notifications) => [notification, ...notifications]);

		if (duration > 0) {
			this.timers.set(notification.id, {
				endAt: Date.now() + duration,
				remaining: duration,
				timeoutId: globalThis.setTimeout(() => this.remove(notification.id), duration),
			});
		}
	}

	private clearTimer(id: number): void {
		const timer = this.timers.get(id);
		if (!timer) {
			return;
		}

		globalThis.clearTimeout(timer.timeoutId);
		this.timers.delete(id);
	}
}
